import { readPageProject, createPageSavePayload, createPageId } from '@/schema/page-project'
import { cloneConfig } from '@/schema/legacy-adapter'
import { navigationIssue } from '@/runtime/page-navigation'
import { uuid } from './utils'
import { screenCreatePayload } from './screen-create'
import { isCanvasProjectType } from './project-types'

export const SCREEN_FILE_LIMIT = 10 * 1024 * 1024
const object = value => value && typeof value === 'object' && !Array.isArray(value)
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key)
const localId = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value)
const componentId = value => (typeof value === 'string' && value.length > 0) || (typeof value === 'number' && Number.isFinite(value))
const fileSize = value => new Blob([value]).size
const sizeError = () => new Error('项目文件不能超过10MB')
const unsafeKeys = ['__proto__', 'prototype', 'constructor']

function assertSize (value) {
  if (fileSize(value) > SCREEN_FILE_LIMIT) throw sizeError()
}

function assertJsonStructure (value) {
  const pending = [{ value, depth: 1 }]
  while (pending.length) {
    const current = pending.pop()
    if (!current.value || typeof current.value !== 'object') continue
    if (current.depth > 100) throw new Error('项目配置嵌套层级过深')
    Object.keys(current.value).forEach(key => {
      if (unsafeKeys.includes(key)) throw new Error('项目配置包含不支持的字段')
      pending.push({ value: current.value[key], depth: current.depth + 1 })
    })
  }
}

function parseJson (text) {
  let value
  try { value = JSON.parse(text) } catch (error) { throw new Error('项目配置不是有效的 JSON') }
  // JSON.parse 负责语法；仅预检对象键和层级，拒绝被“后一个同名字段”掩盖的配置。
  const frames = []
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (char === '"') {
      const start = i++
      while (i < text.length && text[i] !== '"') { if (text[i] === '\\') i++; i++ }
      let after = i + 1
      while (/\s/.test(text[after] || '') && after < text.length) after++
      if (text[after] === ':') {
        const key = JSON.parse(text.slice(start, i + 1)), keys = frames[frames.length - 1]
        if (unsafeKeys.includes(key)) throw new Error('项目配置包含不支持的字段')
        if (keys.has(key)) throw new Error('项目 JSON 存在重复字段：' + key)
        keys.add(key)
      }
    } else if (char === '{' || char === '[') {
      frames.push(char === '{' ? new Set() : null)
      if (frames.length > 100) throw new Error('项目配置嵌套层级过深')
    } else if (char === '}' || char === ']') frames.pop()
  }
  return value
}

function decodeJson (value) {
  let decoded = value
  if (typeof value === 'string') {
    assertSize(value)
    decoded = parseJson(value)
  }
  assertJsonStructure(decoded)
  return decoded
}

// 只清除已知的项目访问保护和编辑临时身份，不递归猜测业务字段或执行配置脚本。
function cleanRecord (record) {
  if (!object(record) || !isCanvasProjectType(record.type)) throw new Error('请选择大屏或2D组态项目 JSON 文件')
  const copy = cloneConfig(record)
  let metadata = copy.metadata
  if (metadata === undefined || metadata === null || metadata === '') metadata = {}
  metadata = decodeJson(metadata)
  if (!object(metadata)) throw new Error('项目配置必须是对象')
  if (object(metadata.visual)) delete metadata.visual.password
  delete metadata.previewControlVersionId
  delete metadata.templatePreview
  copy.metadata = metadata
  return copy
}

function assertCanvas (config) {
  if (!object(config)) throw new Error('页面配置必须是对象')
  for (const key of ['width', 'height']) {
    if (!own(config, key)) continue
    const raw = config[key]
    if (!['number', 'string'].includes(typeof raw) || (typeof raw === 'string' && !raw.trim())) throw new Error('画布尺寸必须是有效数值')
    const value = Number(raw)
    if (!Number.isFinite(value) || value <= 0 || value > 20000) throw new Error('画布尺寸必须大于0且不能超过20000')
  }
}

function walkNodes (nodes, visit, depth = 0) {
  if (!Array.isArray(nodes) || depth > 12) throw new Error('组件树无效或嵌套超过12层')
  nodes.forEach(node => {
    if (!object(node)) throw new Error('组件必须是对象')
    if (!own(node, 'children') && !object(node.component)) throw new Error('组件类型配置必须是对象')
    visit(node)
    if (own(node, 'children')) walkNodes(node.children, visit, depth + 1)
  })
}

function assertPageNodes (nodes, pages, count) {
  const ids = new Set()
  walkNodes(nodes, node => {
    if (++count.value > 3000) throw new Error('项目不能超过3000个组件')
    if (!componentId(node.index) || ids.has(String(node.index))) throw new Error('组件编号无效或同一页面存在重复组件编号')
    ids.add(String(node.index))
  })
  walkNodes(nodes, node => {
    if (node.child !== undefined && node.child !== null) {
      if (!object(node.child)) throw new Error('组件联动配置必须是对象')
      if (own(node.child, 'index')) {
        if (!Array.isArray(node.child.index)) throw new Error('组件联动引用必须是数组')
        node.child.index.forEach(id => {
          if (!componentId(id) || !ids.has(String(id))) throw new Error('组件联动引用不存在')
        })
      }
    }
    if (node.navigation !== undefined && node.navigation !== null) {
      if (!object(node.navigation)) throw new Error('页面跳转配置必须是对象')
      if (node.navigation.type === 'product' && (!Array.isArray(node.navigation.routes) || node.navigation.routes.some(route => !object(route)))) throw new Error('产品跳转规则必须是对象列表')
      const issue = navigationIssue(node.navigation, pages)
      if (issue) throw new Error(issue)
    }
  })
}

// 迁移完整项目，沿用已有读取器；不通过模板白名单裁剪组件和数据配置。
function inspect (record) {
  const clean = cleanRecord(record), raw = clean.metadata.pageProject
  let legacyNodes
  if (own(clean.metadata, 'config')) {
    const wrapper = clean.metadata.config
    if (!object(wrapper)) throw new Error('项目 config 必须是对象')
    if (own(wrapper, 'detail')) assertCanvas(decodeJson(wrapper.detail))
    if (own(wrapper, 'component')) legacyNodes = decodeJson(wrapper.component)
  }
  if (raw !== undefined) {
    if (!object(raw) || raw.version !== 1 || !Array.isArray(raw.pages) || !raw.pages.length || raw.pages.length > 100) throw new Error('多页面配置无效，项目必须包含1到100个页面')
    const ids = new Set()
    raw.pages.forEach(page => {
      if (!object(page) || !localId(page.id) || ids.has(page.id)) throw new Error('页面编号无效或重复')
      ids.add(page.id)
      if (typeof page.name !== 'string' || !page.name.trim() || page.name.length > 60) throw new Error('页面名称必须为1到60个字符')
      assertCanvas(page.config)
    })
    if (!ids.has(raw.homePageId)) throw new Error('项目首页不存在')
    const count = { value: 0 }
    raw.pages.forEach(page => assertPageNodes(page.nodes, raw.pages, count))
  }
  // 现代项目仍附带旧首页镜像；镜像不能夹带坏结构或旧悬空联动。
  if (legacyNodes !== undefined) assertPageNodes(legacyNodes, raw ? raw.pages : [], { value: 0 })
  const model = readPageProject(clean)
  if (model.pageProject.pages.length > 100) throw new Error('导入项目不能超过100个页面')
  const count = { value: 0 }
  model.pageProject.pages.forEach(page => { assertCanvas(page.config); assertPageNodes(page.nodes, model.pageProject.pages, count) })
  return model
}

export function exportScreenProject (record) {
  const model = inspect(record), p = model.pageProject, home = p.pages.find(page => page.id === p.homePageId)
  // 不迁移主键、target、归属分类或发布状态，导入始终创建新记录。
  const metadata = JSON.parse(createPageSavePayload({}, model.metadata, p, home.id, home.config, home.nodes).metadata)
  const value = { format: 'jetlinks-big-screen', version: 1, project: { type: record.type, name: record.name || '未命名项目', description: record.description || '', metadata } }
  // 与实际下载的 UTF-8、缩进格式一致，不能生成自己无法重新导入的文件。
  assertJsonStructure(value)
  assertSize(JSON.stringify(value, null, 2))
  return value
}

export function readScreenFile (text) {
  if (typeof text !== 'string') throw new Error('项目文件必须是 JSON 文本')
  assertSize(text)
  let value
  try { value = parseJson(text.replace(/^\uFEFF/, '')) } catch (error) { throw new Error('项目文件不是有效的 JSON，或包含重复、不支持的字段或过深嵌套') }
  if (!object(value) || value.format !== 'jetlinks-big-screen' || value.version !== 1) throw new Error('项目文件格式或版本不支持，请使用大屏列表导出的 JSON')
  const model = inspect(value.project)
  return { record: cleanRecord(value.project), model }
}

export function importScreenPayload (file, form, pageId = createPageId, nodeId = uuid, type = 'big_screen') {
  if (!object(file) || !object(file.record)) throw new Error('请选择有效的大屏项目文件')
  assertSize(JSON.stringify(file.record))
  const record = screenCreatePayload(form, type), model = inspect(file.record), project = cloneConfig(model.pageProject)
  const used = new Set(project.pages.map(page => page.id))
  project.pages.forEach(page => walkNodes(page.nodes, node => used.add(String(node.index))))
  const wrapper = model.metadata.config, visual = model.metadata.visual
  // 旧 v1 文件可能保留项目身份和首页镜像，只用于避让编号，绝不继承成新项目身份。
  const previousIds = [file.record.id, wrapper && wrapper.id, wrapper && wrapper.visualId, visual && visual.id]
  previousIds.forEach(id => { if (componentId(id)) used.add(String(id)) })
  if (wrapper && own(wrapper, 'component')) walkNodes(decodeJson(wrapper.component), node => used.add(String(node.index)))
  const nextId = generate => {
    for (let attempt = 0; attempt < 20; attempt++) {
      const id = generate()
      if (localId(id) && !used.has(id)) { used.add(id); return id }
    }
    throw new Error('项目内部编号生成冲突，请重新导入')
  }
  const ids = new Map(project.pages.map(page => [page.id, nextId(pageId)]))
  project.homePageId = ids.get(project.homePageId)
  project.pages.forEach(page => {
    page.id = ids.get(page.id)
    const components = new Map()
    walkNodes(page.nodes, node => { components.set(String(node.index), nextId(nodeId)) })
    walkNodes(page.nodes, node => {
      node.index = components.get(String(node.index))
      if (node.child && Array.isArray(node.child.index)) node.child.index = node.child.index.map(id => components.get(String(id)))
      const action = node.navigation
      if (action && action.type === 'page') action.pageId = ids.get(action.pageId)
      if (action && action.type === 'product') {
        action.routes.forEach(route => { route.pageId = ids.get(route.pageId) })
        if (action.fallback && action.fallback.type === 'page') action.fallback.pageId = ids.get(action.fallback.pageId)
      }
    })
  })
  const home = project.pages.find(page => page.id === project.homePageId)
  const payload = { ...record, ...createPageSavePayload(record, model.metadata, project, home.id, home.config, home.nodes) }
  assertSize(payload.metadata)
  return payload
}

export function downloadScreenProject (value, name) {
  assertJsonStructure(value)
  const text = JSON.stringify(value, null, 2)
  assertSize(text)
  const blob = new Blob([text], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob), link = document.createElement('a')
  const filename = Array.from(name || '大屏项目', char => char.charCodeAt(0) < 32 ? '_' : char).join('').replace(/[<>:"/\\|?*]/g, '_')
  link.href = url; link.download = filename.slice(0, 100) + '.json'
  document.body.appendChild(link); link.click(); link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
